// src/features/hmis/components/HMIEdit.tsx - VERSIÓN COMPLETA CORREGIDA
import React, { useState } from 'react';
import {
  Edit,
  SimpleForm,
  TextInput,
  NumberInput,
  ReferenceInput,
  SelectInput,
  required,
  useTranslate,
  useNotify,
  useRedirect,
  useRecordContext,
  LoadingIndicator,
  usePermissions,
  ReferenceField,
  TextField,
  FormDataConsumer,
} from 'react-admin';
import {
  Box,
  Alert,
  Typography,
  Grid,
} from '@mui/material';

// COMPONENTES DEL SISTEMA
import { MyToolbar } from '../../../components/forms/MyToolbar';
import { MyFormContainer } from '../../../components/forms/MyFormContainer';
import { 
  CenteredFormLayout,
  MyFormSection,
} from '../../../components/forms/MyFormLayout';
import BaseDeleteDialog from '../../../components/common/BaseDeleteDialog';

// Hooks y tipos
import { useHMIPermissions } from '../hooks/useHMIPermissions';
import { useHMIValidation } from '../hooks/useHMIValidation';
import { HMIDetailResponse, HMI_TYPE_CHOICES } from '../types';

// Validaciones locales
const validateIpAddress = (value: string) => {
  const ipRegex = /^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;
  if (!value || !ipRegex.test(value)) {
    return 'Formato de dirección IP inválido';
  }
  return undefined;
};

const validateTcpPort = (value: number) => {
  if (!value || value < 1 || value > 65535) {
    return 'El puerto debe estar entre 1 y 65535';
  }
  return undefined;
};

const HMIEditTitle: React.FC = () => {
  const record = useRecordContext<HMIDetailResponse>();
  const translate = useTranslate();
  
  if (!record) return <LoadingIndicator />;
  
  return (
    <span>
      {translate('resources.hmis.titles.edit', 'Editar HMI')}: {record.name}
    </span>
  );
};

const HMIEdit: React.FC = (props) => {
  const { permissions } = usePermissions();
  const translate = useTranslate();
  const notify = useNotify();
  const redirect = useRedirect();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const role = permissions?.role || localStorage.getItem('role');
  if (!role || !['SuperAdmin', 'GroupAdmin', 'HotelAdmin'].includes(role)) {
    return (
      <MyFormContainer maxWidth="md">
        <Alert severity="error">
          {translate('ra.page.forbidden', 'No tienes permisos para acceder a esta página')}
        </Alert>
      </MyFormContainer>
    );
  }

  const transform = (data: any) => {
    const record = data;
    return {
      name: data.name?.trim(),
      type: data.type,
      zoneId: data.zoneId || record.zone?.id,
      controllerIpAddress: data.controllerIpAddress || record.controllerIpAddress || '0.0.0.0',
      controllerTcpPort: typeof data.controllerTcpPort === 'string' 
        ? parseInt(data.controllerTcpPort, 10) 
        : (data.controllerTcpPort || record.controllerTcpPort || 8080),
    };
  };

  const handleSuccess = (data: any) => {
    notify('resources.hmis.messages.update_success', {
      type: 'success',
      messageArgs: { name: data?.name }
    });
    redirect('list', 'hmis');
  };

  const handleError = (error: any) => {
    console.error('Mutation error:', error);
    notify(
      error?.body?.message ||
      error?.message ||
      'resources.hmis.messages.update_error',
      { type: 'error' }
    );
  };

  const handleDeleteClick = () => {
    setDeleteDialogOpen(true);
  };

  return (
    <Edit
      {...props}
      title={<HMIEditTitle />}
      transform={transform}
      mutationOptions={{
        onSuccess: handleSuccess,
        onError: handleError
      }}
      redirect={false}
      mutationMode='pessimistic'
    >
      <HMIEditForm onDelete={handleDeleteClick} />
      
      <HMIDeleteDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      />
    </Edit>
  );
};

const HMIEditForm: React.FC<{ onDelete?: () => void }> = ({ onDelete }) => {
  const record = useRecordContext<HMIDetailResponse>();
  const translate = useTranslate();
  const redirect = useRedirect();
  
  const {
    validateName,
    validateType,
    validateZoneId,
  } = useHMIValidation();
  
  const {
    canEditHMIs,
    canDeleteHMIs
  } = useHMIPermissions();

  if (!record) return <LoadingIndicator />;

  if (!canEditHMIs) {
    return (
      <Alert severity="error">
        {translate('resources.hmis.messages.no_permission_edit')}
      </Alert>
    );
  }

  const handleCancel = () => redirect('list', 'hmis');

  return (
    <MyFormContainer>
      <SimpleForm
        toolbar={
          <MyToolbar
            onCancel={handleCancel}
            resource="hmis"
            showDelete={canDeleteHMIs}
            onDelete={onDelete}
          />
        }
      >
        <CenteredFormLayout>
          <MyFormSection 
            variant='outlined'
            title={translate('resources.hmis.sections.edit', 'Modificar HMI')}
          >
            {/* Nombre del HMI */}
            <TextInput
              source="name"
              label={translate('resources.hmis.fields.name')}
              validate={[required(), validateName]}
              fullWidth
              size="small"
            />

            {/* Tipo y Zona en una línea */}
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <SelectInput
                  source="type"
                  label={translate('resources.hmis.fields.type')}
                  validate={[required(), validateType]}
                  choices={HMI_TYPE_CHOICES}
                  fullWidth
                  size="small"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormDataConsumer>
                  {({ formData }) => (
                    <ReferenceInput
                      source="zoneId"
                      reference="zones"
                      label={translate('resources.hmis.fields.zone')}
                      sort={{ field: 'name', order: 'ASC' }}
                      filter={{ hotelId: formData.hotelId || formData.hotel?.id || record?.hotelId }}
                      key={formData.hotelId}
                    >
                      <SelectInput
                        optionText="name"
                        fullWidth
                        size="small"
                        validate={[required(), validateZoneId]}
                      />
                    </ReferenceInput>
                  )}
                </FormDataConsumer>
              </Grid>
            </Grid>

            {/* IP del Controlador y Puerto TCP */}
            <Grid container spacing={2}>
              <Grid item xs={12} md={8}>
                <TextInput
                  source="controllerIpAddress"
                  label={translate('resources.hmis.fields.controllerIpAddress')}
                  validate={[required(), validateIpAddress]}
                  fullWidth
                  size="small"
                  placeholder="192.168.1.100"
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <NumberInput
                  source="controllerTcpPort"
                  label={translate('resources.hmis.fields.controllerTcpPort')}
                  validate={[required(), validateTcpPort]}
                  fullWidth
                  size="small"
                  min={1}
                  max={65535}
                />
              </Grid>
            </Grid>

          </MyFormSection>
        </CenteredFormLayout>
      </SimpleForm>
    </MyFormContainer>
  );
};

const HMIDeleteDialog: React.FC<{
  open: boolean;
  onClose: () => void;
}> = ({ open, onClose }) => {
  const record = useRecordContext<HMIDetailResponse>();
  const translate = useTranslate();

  if (!record) return null;

  return (
    <BaseDeleteDialog
      open={open}
      onClose={onClose}
      recordId={record.id}
      resource="hmis"
      warningMessage={
        record.isOnline
          ? translate('resources.hmis.messages.delete_while_online', {
              name: record.name
            })
          : undefined
      }
    />
  );
};

export default HMIEdit;